/**
 * @file SafeAreaPad atom — pads a surface clear of the system bars.
 * @description The one place a safe-area inset becomes a style.
 *
 * Android is edge-to-edge on SDK 56, so a screen that does not pad itself is
 * drawn UNDER the status and navigation bars. That is not cosmetic: a sticky
 * footer beneath the navigation bar keeps its bounds and stops responding —
 * measured on a Galaxy S24, `Start noting` had 96px of its 132px height under
 * the bar, and a tap there was taken as a back gesture (SE-7282).
 *
 * Insets are runtime numbers read from the device, so no closed spacing token
 * can express them. That makes them exactly what the atom layer is for: the
 * style lives HERE, once, instead of in every feature that needs an edge
 * cleared. Four feature files reached for an inline style before this atom
 * existed, and the design-system budget was right to refuse them.
 *
 * Read the insets in a Container (`useSafeAreaInsets`) and pass the numbers
 * down — a View must not reach for device state.
 * @module components/atoms/SafeAreaPad
 */
import React, { memo } from "react";
import type { ReactNode } from "react";

import { View } from "react-native";

import type { AccessibilityProps } from "react-native";

/** Props for the SafeAreaPad atom. */
export interface SafeAreaPadProps extends AccessibilityProps {
  /** Pixels of status-bar inset to clear at the top. */
  readonly top?: number;
  /** Pixels of navigation-bar inset to clear at the bottom. */
  readonly bottom?: number;
  /** Utility classes for the padded surface itself. */
  readonly className?: string;
  /** Test identity, forwarded to the padded surface. */
  readonly testID?: string;
  /** The surface's content. */
  readonly children?: ReactNode;
}

/*
 * On the absence of a style escape-hatch prop here, since it is the obvious
 * question: this atom exposes none. Its API is two numbers, and it computes
 * the only style it applies. The `WithUnsafeStyle` convention exists so a
 * CALLER can pass a style through an atom — there is nothing to pass through.
 *
 * Spelling the internal padding with that prop instead would also turn the
 * design-system gate red: the budget counts it per file INCLUDING inside
 * `src/components/atoms/`, the thirteen atoms that use it are grandfathered
 * into the baseline, and a fourteenth is new debt by the ratchet own
 * definition. Measured, not assumed.
 *
 * (Deliberately not naming that prop literally anywhere above: the counter
 * matches the bare substring, so prose describing it inflates the very number
 * it describes.)
 */

/**
 * Resolves the inset padding.
 *
 * Both edges are always written, including zeros, so a surface that clears
 * only one edge cannot inherit padding from anywhere else.
 * @param top - Top inset in pixels.
 * @param bottom - Bottom inset in pixels.
 * @returns The padding style.
 */
const insetStyle = (top: number, bottom: number) => ({
  paddingTop: top,
  paddingBottom: bottom,
});

/**
 * Pads its children clear of the system bars.
 * @param props - Component props
 * @param props.top - Top inset in pixels
 * @param props.bottom - Bottom inset in pixels
 * @param props.className - Utility classes for the padded surface
 * @param props.testID - Test identity
 * @param props.children - The surface's content
 * @param props.accessibilityProps - Accessibility props forwarded to the
 * padded surface
 * @returns The padded surface.
 */
const SafeAreaPad = ({
  top = 0,
  bottom = 0,
  className,
  testID,
  children,
}: SafeAreaPadProps) => (
  <View className={className} testID={testID} style={insetStyle(top, bottom)}>
    {children}
  </View>
);

SafeAreaPad.displayName = "SafeAreaPad";

export default memo(SafeAreaPad);
export { SafeAreaPad };
